import type { Client } from "#/types/client";
import type { Item } from "#/types/items";
import { StatusType } from "#/types/items";
import { daysSince } from "#/utils/dateTimeUtils";

export interface DwellStatus {
  /** Current status is classified as internal (within a warehouse) for the client. */
  isInternal: boolean;
  /** Days the item has been in its current status (since Last Moved). */
  idleDays: number | null;
  /** The category's configured dwell time, in days. */
  dwellDays: number | null;
  /** Internal item that has sat in its status at least `dwellDays`. */
  isStuck: boolean;
}

/**
 * Evaluates whether an item is "stuck": an internal-type item that has been in
 * its current status for at least its category's dwell time. Centralised so the
 * items table and item detail page share one definition.
 */
export const getDwellStatus = (
  item: Pick<Item, "status" | "last_moved" | "product">,
  client: Client | null | undefined,
): DwellStatus => {
  // An item with no status yet has no status type, so it can never be stuck.
  const isInternal =
    item.status != null && client?.statusTypes?.[item.status] === StatusType.INTERNAL;
  const idleDays = daysSince(item.last_moved);
  const dwellDays = item.product?.dwell_time ?? null;
  const isStuck =
    isInternal && dwellDays != null && idleDays != null && idleDays >= dwellDays;

  return { isInternal, idleDays, dwellDays, isStuck };
};
